import { z } from "zod";

const HERO_COMPONENT_TYPES = new Set(["Hero", "hero", "hero-section"]);
const TRUST_SIGNAL_COMPONENT_TYPES: Record<string, string> = {
  Testimonials: "testimonials",
  SocialProof: "social proof",
  SocialProofPopup: "social proof popup",
  LogoCloud: "logo cloud",
  ClientLogos: "client logos",
  StatsCounter: "stats",
  Counter: "stats",
  Awards: "awards",
  CaseStudy: "case study",
};

const threeSecondSeveritySchema = z.enum(["critical", "warning", "info"]);

export const threeSecondTestIssueSchema = z.object({
  criterion: z.string().min(1).max(120),
  severity: threeSecondSeveritySchema,
  suggestion: z.string().min(1).max(400),
});

export const threeSecondTestResultSchema = z.object({
  score: z.number().min(0).max(100),
  issues: z.array(threeSecondTestIssueSchema).max(10),
});

export const threeSecondTestInputSchema = z.object({
  headline: z.string().min(1).max(200),
  subheadline: z.string().max(500).optional(),
  ctaText: z.string().max(120).optional(),
  backgroundSummary: z.string().max(240).optional(),
  trustSignals: z.array(z.string().min(1).max(80)).max(8).default([]),
  pageComponentTypes: z.array(z.string().min(1).max(80)).max(100).default([]),
});

export type ThreeSecondTestInput = z.infer<typeof threeSecondTestInputSchema>;
export type ThreeSecondTestResult = z.infer<typeof threeSecondTestResultSchema>;
export type ThreeSecondTestIssue = z.infer<typeof threeSecondTestIssueSchema>;

export interface ThreeSecondTestGatewayResponse {
  content: string;
  usage?: { inputTokens: number; outputTokens: number };
}

export interface ThreeSecondTestGatewayRequest {
  tenantId: string;
  userId: string;
  systemPrompt: string;
  userPrompt: string;
}

export interface ThreeSecondTestServiceDeps {
  complete(request: ThreeSecondTestGatewayRequest): Promise<ThreeSecondTestGatewayResponse>;
}

type PuckLikeComponent = {
  type?: string | null;
  props?: Record<string, unknown> | null;
};

type PuckLikeData = {
  content?: PuckLikeComponent[] | null;
};

function readString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function summarizeBackground(props: Record<string, unknown>): string | undefined {
  const dssStyles = props._dssStyles;
  if (!dssStyles || typeof dssStyles !== "object") {
    return undefined;
  }

  const background = (dssStyles as { background?: unknown }).background;
  if (!background || typeof background !== "object") {
    return undefined;
  }

  const mode = readString((background as { mode?: unknown }).mode);
  const imageUrl = readString((background as { image?: { url?: unknown } }).image?.url);
  const videoUrl = readString((background as { video?: { url?: unknown } }).video?.url);

  if (mode === "gradient") {
    return "gradient background";
  }

  if (imageUrl) {
    return "background image";
  }

  if (videoUrl) {
    return "background video";
  }

  return mode ? `${mode} background` : undefined;
}

function extractTrustSignals(content: PuckLikeComponent[]): string[] {
  const signals = new Set<string>();

  for (const component of content) {
    const type = readString(component.type);
    if (!type) {
      continue;
    }

    const signal = TRUST_SIGNAL_COMPONENT_TYPES[type];
    if (signal) {
      signals.add(signal);
    }
  }

  return [...signals];
}

export function extractThreeSecondTestInput(
  puckData: PuckLikeData | null | undefined,
): ThreeSecondTestInput | null {
  const content = Array.isArray(puckData?.content) ? puckData.content : [];
  const hero = content.find((component) => {
    const type = readString(component.type);
    return type ? HERO_COMPONENT_TYPES.has(type) : false;
  });

  const props = hero?.props;
  if (!props || typeof props !== "object") {
    return null;
  }

  const headline = readString(props.title) ?? readString(props.heading) ?? readString(props.headline);
  if (!headline) {
    return null;
  }

  const input = {
    headline,
    subheadline: readString(props.subtitle) ?? readString(props.subheading) ?? readString(props.description),
    ctaText: readString(props.ctaLabel) ?? readString(props.ctaText) ?? readString(props.buttonText),
    backgroundSummary: summarizeBackground(props),
    trustSignals: extractTrustSignals(content),
    pageComponentTypes: content
      .map((component) => readString(component.type))
      .filter((value): value is string => Boolean(value)),
  };

  return threeSecondTestInputSchema.parse(input);
}

export function buildThreeSecondTestSystemPrompt(): string {
  return [
    "You are a landing-page conversion reviewer running a strict 3-second test on hero content.",
    "Score only these five criteria: headline clarity, CTA visibility, value proposition, visual hierarchy, trust signal.",
    "Return JSON only with this exact shape:",
    '{"score": number, "issues": [{"criterion": string, "severity": "critical" | "warning" | "info", "suggestion": string}]}',
    "Rules:",
    "- score must be 0-100",
    "- issues must be actionable and concise",
    "- use one issue per real problem, not generic praise",
    "- if a criterion is strong, omit it from issues instead of adding filler",
    "- trust signal can be satisfied by testimonials, logo clouds, stats, awards, case studies, or equivalent proof",
  ].join("\n");
}

export function buildThreeSecondTestUserPrompt(input: ThreeSecondTestInput): string {
  const safeInput = threeSecondTestInputSchema.parse(input);

  return [
    "Review this hero for a first-impression 3-second test.",
    `Headline: ${safeInput.headline}`,
    `Subheadline: ${safeInput.subheadline ?? "missing"}`,
    `CTA: ${safeInput.ctaText ?? "missing"}`,
    `Background: ${safeInput.backgroundSummary ?? "unspecified"}`,
    `Trust signals on page: ${safeInput.trustSignals.length > 0 ? safeInput.trustSignals.join(", ") : "none detected"}`,
    `Page component types: ${safeInput.pageComponentTypes.length > 0 ? safeInput.pageComponentTypes.join(", ") : "hero only"}`,
  ].join("\n");
}

function parseJsonCandidate(raw: string): unknown {
  const fencedMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = fencedMatch?.[1] ?? raw;
  return JSON.parse(candidate.trim());
}

export function parseThreeSecondTestResult(raw: string): ThreeSecondTestResult {
  const parsed = parseJsonCandidate(raw);
  return threeSecondTestResultSchema.parse(parsed);
}

export function createThreeSecondTestService(deps: ThreeSecondTestServiceDeps) {
  return {
    async run(
      tenantId: string,
      input: ThreeSecondTestInput,
      userId: string,
    ): Promise<ThreeSecondTestResult> {
      const safeInput = threeSecondTestInputSchema.parse(input);
      const response = await deps.complete({
        tenantId,
        userId,
        systemPrompt: buildThreeSecondTestSystemPrompt(),
        userPrompt: buildThreeSecondTestUserPrompt(safeInput),
      });

      return parseThreeSecondTestResult(response.content);
    },
  };
}
